import { useParams, useNavigate, Link } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { BookingScheduleBanner } from "@/components/booking-schedule-banner";
import { DetailErrorState } from "@/components/detail-error-state";
import { ServiceClassificationChip } from "@/components/service-classification-chip";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BudgetChip } from "@/pages/customers/budget/chip";
import { workCategory } from "@/pages/customers/budget/model";
import { api } from "@/lib/api";
import { ImageLightbox } from "@/components/image-lightbox";
import { useAuth } from "@/lib/auth";
import { CancelJobDialog } from "@/components/cancel-job-dialog";
import { ReopenJobDialog } from "@/components/reopen-job-dialog";
import { SendBackToFieldDialog } from "@/components/send-back-to-field-dialog";
import { TaskCreateDialog } from "@/components/task-create-dialog";
import { TaskListInline } from "@/components/task-list-inline";
import { FromTheFieldSection } from "@/components/field-add/field-add-office";
import { JobTaskFlagStrip } from "@/components/job-task-flag";
import { JobCheckupStrip } from "@/components/job-checkup-strip";
import { SendCustomerDialog } from "@/components/send-customer-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { PageSpinner } from "@/components/ui/spinner";
import { DataTable, type Column } from "@/components/ui/data-table";
import { usePageContextReporter } from "@/lib/page-context";
import { cn, statusColor, formatDate, formatDateTime } from "@/lib/utils";
import { deviceCountMismatch, tradeExpectsDevices } from "@/lib/vocabulary";
import { customerWoNumber } from "@/lib/wo-number";
import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import {
    ArrowLeft,
    User,
    MapPin,
    ShieldCheck,
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    Clock,
    ClipboardList,
    Pencil,
    Plus,
    Ban,
    MoreHorizontal,
    UserCheck,
    Calendar,
    Phone,
    RotateCcw,
} from "lucide-react";
import {
    deriveChapters,
    deriveStages,
    isPostVisit,
    packetSubjectFor,
    primaryHeaderActions,
    STAGE_CHAPTER,
    type ChapterKey,
    type HeaderAction,
    type JobStage,
} from "./jobDetailHelpers";
import { ChapterShell } from "./detail/chapter";
import { StageStrip } from "./detail/stage-strip";
import { TimeOnSite } from "./detail/time-on-site";
import { ArrivalLag, LastReachedUs } from "./detail/arrival-lag";
import { ResultsCard } from "./detail/results-card";
import { AddDevicePicker, EditDeviceModal } from "./detail/device-dialogs";
import { useJobMutations } from "./detail/use-job-mutations";
import { RecordRepairDialog } from "./detail/record-repair-dialog";
import { RemoveRepairDialog } from "./detail/remove-repair-dialog";
import { ReconcileFromListDialog } from "../sites/reconcile-from-list";
import { PaperworkCard } from "./detail/paperwork-card";
import { PermitCard } from "./detail/permit-card";
import { AfterHoursCard } from "./detail/after-hours-card";
import { RegistrationWarningsCard } from "./detail/registration-warnings";
import { JobLineageStrip } from "./detail/lineage-strip";
import { JobPartsCard } from "./detail/parts-card";
import { JobPoCard } from "./detail/po-card";
import { NowPanel, useJobNeeds } from "./detail/now-panel";
import { FinishLinePanel, type FinishLineSection } from "./detail/finish-line-panel";
import { isPreVisit } from "./detail/finishLineModel";
import { finishLineKey, useFinishLineTop } from "@/lib/finish-line";
import { JobPhotosCard } from "./detail/photos-card";
import { ActivityCard } from "./detail/activity-card";
import { useJobFailProtocols } from "./detail/fail-protocol-card";
import { QuoteScopeOffer } from "./detail/summary-card";
import { OfficeTechPlanCard } from "@/components/tech-plan/office-tech-plan";
import { JobProofControls, useJobProof } from "@/components/proof/job-proof";
import { ResendPacketDialog } from "@/components/resend-packet-dialog";
import { useSendHistory } from "@/lib/use-send-history";
import { PaperConversionDialog } from "./detail/paper-conversion-dialog";
import { useUserPreference } from "@/lib/user-preference";
import { JobPageVersionToggle } from "./detail/job-page-version-toggle";
import { ReferenceRail } from "./detail/reference-rail";
import { useMakeOfficial, type ReportDeviceCandidate } from "@/components/report-device-picker";
import { useReportMatchAll } from "@/components/report-match-all";
import { CalmJobBody, type CalmRequest, type CalmRequestKind, type JobDetailsMode } from "./detail/calm/calm-body";
import { DetailsCard, ScheduleCrewCard, WorkOrderMappingCard } from "./detail/reference-rail";
import {
    jobActions,
    type JobAction,
    type JobActionContext,
    type JobActionHandlers,
    type MoveTarget,
} from "./detail/jobActions";
import { JobGlance } from "./detail/job-glance";
import { JobCustomerCheck } from "@/components/move-job-customer";
import { JobHandoffStrip } from "@/components/closeout/handoff";
import type { DeviceFocus } from "./detail/jobGlanceModel";
import { useJobPins } from "./detail/use-job-pins";
import { openCommandPalette, publishJobActions, type JobActionsPublication } from "@/lib/job-actions-bus";
import { openPriceCheck } from "@/components/price-check";
import { openHelpDesk } from "@/lib/use-job-checkup";
import { openSupportCopilot } from "@/components/support-copilot";
import { openExternalLink } from "@/lib/standalone";
import { setJobPageVariant } from "@/lib/api-screen";
import {
    apiErrorInfo,
    assetKindsForTrade,
    visibleJobAssets,
    type DeviceRow,
    type JobAssetRow,
    type JobDetailResponse,
    type TechOption,
} from "./detail/types";
import { JobMarksStrip } from "./detail/job-marks-strip";
import { JobCardHoldPanel } from "@/components/card-hold/card-hold";
import { SiteCheckPick } from "./detail/site-check-pick";
import { isWorkOrderLooking } from "@/lib/signed-work-order";
import { NoWorkOrderBanner, useUseAsOriginalWorkOrder, type WorkOrderOffer } from "./detail/no-work-order-banner";
import { TeachIntro } from "@/teach/teach-card";
import { TeachDots } from "@/teach/teach-dot";

export default function JobDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { isOffice, can: canDo } = useAuth();
    // #1250 — the permit route is requirePermission(jobs.manage), so gate the
    // card's inputs on the PERMISSION, not the role. Otherwise an office user
    // without it gets editable fields and a raw "Missing permission 'jobs.manage'".
    const canManageJobs = canDo("jobs.manage");
    // #1300 — the "Use the paper" rip cord is OFFICE OR ABOVE. Gated on the
    // same permission as the server route (office_review.manage), deliberately
    // NOT jobs.manage: Lead Tech holds that one, and a field role must never be
    // able to convert its own paperwork into a filed result.
    const canConvertPaper = canDo("office_review.manage");
    /** The device whose signed paper is being recorded (#1300); null = closed. */
    const [paperAsset, setPaperAsset] = useState<JobAssetRow | null>(null);
    // Office "Record repair for <tech>" — the repair device whose dialog is open.
    const [repairAsset, setRepairAsset] = useState<JobAssetRow | null>(null);
    // Office "Remove repair" — the device whose repair is being removed.
    const [removeRepairAsset, setRemoveRepairAsset] = useState<JobAssetRow | null>(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showReopenDialog, setShowReopenDialog] = useState(false);
    // #815: "Send back to field" collects a required why-note that reaches
    // the tech (urgent task + push + SMS) along with the status flip.
    const [showSendBackDialog, setShowSendBackDialog] = useState(false);
    /** Bumped when the header "Schedule" needs the Schedule & Crew editor open. */
    const [scheduleEditNonce, setScheduleEditNonce] = useState(0);
    const [showTaskDialog, setShowTaskDialog] = useState(false);
    // Document upload (Paperwork card header).
    const [isUploadingDoc, setIsUploadingDoc] = useState(false);
    // Inline title editing.
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleInput, setTitleInput] = useState("");
    // Device management — lifted from the old JobDevicesPanel so the Results
    // card header can drive the same picker/edit modal.
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerTab, setPickerTab] = useState<"existing" | "quick">("existing");
    const [editingAsset, setEditingAsset] = useState<JobAssetRow | null>(null);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    // Job-type list is only fetched once the Details card enters edit mode.
    const [wantJobTypes, setWantJobTypes] = useState(false);
    // Story chapters (Issue #768): receipt/ghost expansion follows the user
    // across machines; the current stage's chapter is always open regardless.
    const [chapterPrefs, setChapterPrefs] = useUserPreference<Partial<Record<ChapterKey, boolean>>>(
        "job-detail.chapters-open",
        {},
    );
    // "More about this job" demoted group (description · replacements ·
    // legacy devices · warranties) — collapsed by default, session-local.
    const [moreOpen, setMoreOpen] = useState(false);

    // One call returns the job header, legacy device rows (point-pay), and
    // the modern site-spined asset linkage. The UI combines them so it
    // doesn't matter which intake flow created the job.
    const {
        data: jobResp,
        isLoading,
        error: jobError,
        refetch: refetchJob,
    } = useQuery({
        queryKey: ["job", id],
        queryFn: () => api.get<JobDetailResponse>(`/api/jobs/${id}`),
        enabled: !!id,
    });

    // Per-device backflow report PDFs ("[Test Results] SN-... BFT.pdf").
    const testReportsQ = useQuery({
        queryKey: ["job", id, "test-reports"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=test_report`,
            ),
        enabled: !!id,
    });

    // Purveyor-portal official test-report PDFs filed back through the drop
    // zone (category=test_report_official) — Test-type paperwork alongside
    // the generated per-device reports.
    const officialReportsQ = useQuery({
        queryKey: ["job", id, "official-test-reports"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=test_report_official`,
            ),
        enabled: !!id,
    });

    // Job-level inspection report PDFs (e.g. fire-safety summary).
    const inspectionReportsQ = useQuery({
        queryKey: ["job", id, "inspection-reports"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=report`,
            ),
        enabled: !!id,
    });

    // Online Reporting Cheat Sheet (category=summary) — for online-portal
    // jurisdictions (BSI, Vepo, …) this is the job's ONLY generated document
    // (no TCEQ render), so the Paperwork card must surface it.
    const summaryReportsQ = useQuery({
        queryKey: ["job", id, "summary-reports"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=summary`,
            ),
        enabled: !!id,
    });

    // Completed vendor work-order PDFs (category=work_order_filled) —
    // generated on tech submit (or the manual PDF editor). The return
    // document the office forwards to the vendor.
    const workOrdersQ = useQuery({
        queryKey: ["job", id, "work-orders-filled"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=work_order_filled`,
            ),
        enabled: !!id,
    });

    // Closeout "Additional files" (category=closeout_upload) and paper completed
    // WOs from the Work Order slot (category=work_order_uploaded). Neither was
    // shown on the job page, so a signed WO dropped in Additional files was
    // invisible here — and a paper WO in the slot too (threads/wo-in-wrong-slot).
    const closeoutUploadsQ = useQuery({
        queryKey: ["job", id, "closeout-uploads"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=closeout_upload`,
            ),
        enabled: !!id,
    });
    const uploadedWorkOrdersQ = useQuery({
        queryKey: ["job", id, "work-orders-uploaded"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string }> }>(
                `/api/attachments?jobId=${id}&category=work_order_uploaded`,
            ),
        enabled: !!id,
    });

    // Office-uploaded documents (category=document) — distinct from the
    // auto-generated test/inspection report PDFs above.
    const documentsQ = useQuery({
        queryKey: ["job", id, "documents"],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string; file_name: string; created_at: string; file_size?: number }> }>(
                `/api/attachments?jobId=${id}&category=document`,
            ),
        enabled: !!id,
    });

    // Technicians for the assignee picker (same source dispatch uses).
    // Jurisdiction fail protocols — folded into their device rows on the
    // Results card (Issue #768; the standalone card slot is retired).
    const failProtocols = useJobFailProtocols(id ?? "");

    const techsQ = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: TechOption[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
    });

    const jobTypesQ = useQuery({
        queryKey: ["job-types", "active"],
        queryFn: () =>
            api.get<{ jobTypes: Array<{ id: string; name: string; trade_name?: string | null }> }>(
                "/api/job-types?activeOnly=true",
            ),
        enabled: wantJobTypes,
        staleTime: 5 * 60_000,
    });

    const job = jobResp?.job;

    // Support Copilot page context — the Help drawer names this job unprompted.
    usePageContextReporter(
        job
            ? {
                  label: `Job #${job.display_number || job.id.slice(0, 8)}${job.title ? ` — ${job.title}` : ""}`,
                  entity: { type: "jobs", id: job.id },
                  values: { status: job.status, scheduled_start: job.scheduled_start ?? null },
              }
            : null,
    );

    // #1545 — what this customer has already been sent. Office-only, and only
    // once the job has actually been out in the world: a job still being
    // scheduled has no packet history to ask about. `everSent` from this read is
    // the gate on the whole resend feature — never job status, because the
    // FIRST send is the one that flips the job, issues the draft invoice and can
    // charge a card on file, and that stays in close-out behind those gates.
    const sendHistoryQ = useSendHistory(
        job?.id,
        isOffice && !!job && !["draft", "awaiting_scheduling", "unqueued", "cancelled"].includes(job.status),
    );
    const canResendPacket = isOffice && (sendHistoryQ.data?.everSent ?? false);

    const legacyDevices = jobResp?.devices ?? [];
    const rawJobAssets = jobResp?.assets ?? [];
    // Trade filter mirror of the server's reconcile: backflow jobs only
    // render backflow assets (NULL asset_type grandfathered — ST imports
    // pre-date the taxonomy), fire_ext jobs only render extinguisher /
    // e_light. The server is authoritative (it auto-unlinks mismatches);
    // this client filter is defensive for the brief window before /
    // during the reconcile. It MUST stay exactly as permissive as the
    // server's rule: a stricter filter here hid successfully linked
    // devices while the picker kept offering them (7-Eleven #35515). A
    // NULL / unknown trade does NOT discard the list — the server keeps
    // those links and never unlinks them, so we still show what's linked
    // (issue #179: a device added to a trade-less repair job must appear);
    // a job with nothing linked simply reads empty.
    const jobAssets = visibleJobAssets(job?.trade_code ?? null, rawJobAssets);
    const linkedInvoices = jobResp?.invoices ?? [];
    const linkedEstimates = jobResp?.estimates ?? [];
    const testResults = jobResp?.testResults ?? [];
    // Paperwork by device (2026-10-09): the ✓/✗ strip on the Paperwork card.
    const devicePaperwork = useMemo(
        () => ({
            devices: jobAssets.map((a) => ({
                assetId: a.asset_id,
                serial: a.serial_number,
                size: a.size_label,
                assemblyType: a.assembly_type ?? null,
            })),
            tests: testResults.map((t) => ({
                assetId: t.asset_id,
                serial: t.serial_number ?? null,
                result: t.overall_result,
            })),
        }),
        [jobAssets, testResults],
    );
    const repairResults = jobResp?.repairResults ?? [];
    // The NOW panel's "What needs you" list — read here too so the check-up
    // strip's "N things in the way" counts exactly the rows listed under it.
    const jobNeeds = useJobNeeds({
        job,
        isOffice,
        deviceCount: jobAssets.length,
        invoices: linkedInvoices,
        packetMissedOnLastSend: sendHistoryQ.data?.missedOnLastSend ?? null,
    });

    // #1714 — the Finish Line at the top of the page, behind a per-user
    // switch (default off). "off" whenever the switch is off OR the read fails
    // (a 403 included): the page then draws today's top, unchanged.
    const flTop = useFinishLineTop(id);
    // Proof chips S3 (trust audit Phase 2): "how sure" on each leg's result and
    // each line of What the tech will do. On for everyone; the per-person
    // switch is an opt-out, and with it off nothing is fetched.
    const jobProof = useJobProof(id);
    const flActive = flTop.state !== "off";
    // Job page v2 (slice 6): API calls from this page say which version it
    // is (X-GWC-Screen → audit_events.metadata.screen). Backend only.
    useEffect(() => {
        setJobPageVariant(flActive ? "new" : "classic");
        return () => setJobPageVariant(null);
    }, [flActive]);
    // #1717 — with the switch on, the rest of the page is the calm layout. The
    // rail's Schedule & Crew and Details live in a "Job details" drawer; every
    // "open the schedule editor" (header Schedule, the header date and tech,
    // the Finish Line's visit-booked action) opens it at the editor.
    // Job page v2 (slice 4): a "Job details" / "Change date or tech" ask opens
    // the side sheet's Job details or Visit box in place (no drawer).
    const [detailsMode, setDetailsMode] = useState<{ mode: JobDetailsMode; n: number } | null>(null);
    const openDetails = (mode: JobDetailsMode) => setDetailsMode((d) => ({ mode, n: (d?.n ?? 0) + 1 }));
    const [filesNonce, setFilesNonce] = useState(0);
    // Job page v2: what a job action (⌘K, ⋯, a pin) asked the lower half to
    // open, and the Upload action's file picker.
    const [calmRequest, setCalmRequest] = useState<CalmRequest | null>(null);
    // Job page v3: a header device tile asked the Devices list for a group.
    const [devicesFocus, setDevicesFocus] = useState<{ group: DeviceFocus; n: number } | null>(null);
    const jobPins = useJobPins();
    // Hold from ⌘K opens Move to ▾ at "Hold · why?".
    const [holdAsk, setHoldAsk] = useState(0);
    // ⌘K's "This job" group reads this page's actions while it's open.
    const jobActionsPub = useRef<JobActionsPublication | null>(null);
    useEffect(() => {
        publishJobActions(jobActionsPub.current);
    });
    useEffect(() => () => publishJobActions(null), []);
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (flActive && scheduleEditNonce > 0) openDetails("schedule");
        // Only a new bump opens it; switching the page on must not.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleEditNonce]);

    const { invalidateJob, updateStatus, railMutations, assignmentDialog } = useJobMutations(id, job, {
        onFieldsSaved: () => setEditingTitle(false),
    });

    // H21: an invoice auto-created from a completed job can sit unsent (status
    // draft/open) and never reach the customer. Offer Send right on the job.
    const [sendInvoiceId, setSendInvoiceId] = useState<string | null>(null);
    // #1545 — one dialog for the whole page. The header button, the Paperwork
    // packet strip and the NOW panel's "never got it" row all open THIS one, so
    // the three doors can never drift into three different send screens.
    const [resendOpen, setResendOpen] = useState(false);
    const [resendOnlyMissed, setResendOnlyMissed] = useState(false);
    const openResend = (onlyMissed = false) => {
        setResendOnlyMissed(onlyMissed);
        setResendOpen(true);
    };

    const deleteDoc = useMutation({
        mutationFn: (attachmentId: string) => api.delete(`/api/attachments/${attachmentId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", id, "documents"] });
            qc.invalidateQueries({ queryKey: finishLineKey(id) });
            toast.success("Document deleted");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            toast.error(`Delete failed: ${message ?? code ?? "unknown"}`);
        },
    });

    // Unlink an asset from the job (Results card row action).
    const removeAsset = useMutation({
        mutationFn: (linkId: string) => api.delete(`/api/jobs/${id}/assets/${linkId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", id] });
            toast.success("Device removed from job");
        },
        onError: () => toast.error("Couldn't remove device"),
    });

    // #812 — office Test/Repair/Replace scoping. required_action drives the
    // tech's whole experience of a device (the field app buckets by it and
    // 'replace' opens the guided swap flow); until this control the office
    // had no way to set it and every link defaulted to 'test'.
    const scopeAsset = useMutation({
        mutationFn: ({ assetId, action }: { assetId: string; action: "test" | "repair" | "replace" }) =>
            api.patch(`/api/jobs/${id}/assets/${assetId}`, { requiredAction: action }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["job", id] });
            toast.success(
                vars.action === "replace"
                    ? "Scoped as Replace — the tech gets the guided replacement flow on this device."
                    : vars.action === "repair"
                      ? "Scoped as Repair."
                      : "Scoped as Test.",
            );
        },
        onError: (err: unknown) => {
            const info = apiErrorInfo(err);
            toast.error(info.message ? `Couldn't change the scope: ${info.message}` : "Couldn't change the scope");
        },
    });

    // "Which device is this report for?" — a report that names no single
    // device on this job, or a plain document the office turns into one.
    const makeOfficial = useMakeOfficial(id ?? "");
    // "Match all reports" (2026-10-09): every loose PDF on the job in one look.
    // Also what an upload opens when any file didn't file itself — the one-card
    // "Which device?" question only held the LAST file of a multi-file drop.
    const matchAll = useReportMatchAll(id ?? "");
    // "This looks like a work order. Use it as the work order?" (2026-10-21).
    const useAsOriginalWo = useUseAsOriginalWorkOrder(id ?? "");

    const handleDocUpload = async (files: FileList | null) => {
        if (!files || files.length === 0 || !id) return;
        setIsUploadingDoc(true);
        try {
            // #1455: a PDF that is really a test report files as the device's
            // official report (server-side), not as a plain document. Say which
            // happened, and re-pull the report lists + the closeout queue.
            let filedReports = 0;
            let plainDocs = 0;
            let needsDevice = 0;
            for (const file of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("jobId", id);
                fd.append("category", "document");
                const r = await api.postForm<{
                    attachment?: { id: string; file_name: string } | null;
                    filedAsReport?: { serial: string; label: string; duplicate?: boolean } | null;
                    reportNotice?: string | null;
                    needsDevice?: { attachmentId: string; candidates: ReportDeviceCandidate[] } | null;
                    notReport?: { attachmentId: string; docClass: string } | null;
                    workOrderOffer?: WorkOrderOffer | null;
                }>("/api/attachments/upload", fd);
                if (r?.filedAsReport) {
                    filedReports += 1;
                    toast.success(
                        r.filedAsReport.duplicate
                            ? `That report for S/N ${r.filedAsReport.serial} was already on the job — nothing added`
                            : `Filed as the test report for S/N ${r.filedAsReport.serial}`,
                    );
                } else if (r?.needsDevice) {
                    // A report that names no single device on the job. Counted,
                    // and answered all together in "Match all reports" below
                    // (it also finds a device that is at the site but not on
                    // the job yet).
                    needsDevice += 1;
                } else {
                    plainDocs += 1;
                    if (r?.workOrderOffer) {
                        // A vendor work order in the wrong slot: one tap fixes it.
                        useAsOriginalWo.offer(r.workOrderOffer, file.name);
                        plainDocs -= 1;
                    } else if (r?.notReport && file.name.toLowerCase().endsWith(".pdf")) {
                        const asReport = { id: r.notReport.attachmentId, file_name: file.name };
                        toast.success(
                            r.notReport.docClass === "work_order"
                                ? `${file.name} reads as a work order, not a city report — saved as a document.`
                                : `${file.name} saved as a document.`,
                            {
                                duration: 9000,
                                action: { label: "It's a report", onClick: () => makeOfficial.start(asReport) },
                            },
                        );
                        plainDocs -= 1;
                    } else if (r?.reportNotice) toast.warning(r.reportNotice, { duration: 9000 });
                }
            }
            if (plainDocs > 0 && filedReports === 0) {
                toast.success(`${plainDocs} document${plainDocs === 1 ? "" : "s"} uploaded`);
            }
            if (needsDevice > 0) matchAll.open();
            qc.invalidateQueries({ queryKey: ["job", id, "documents"] });
            qc.invalidateQueries({ queryKey: ["job", id, "official-test-reports"] });
            qc.invalidateQueries({ queryKey: finishLineKey(id) });
            qc.invalidateQueries({ queryKey: ["close-out-queue"] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        } catch (err: unknown) {
            const { message } = apiErrorInfo(err);
            toast.error(`Upload failed: ${message ?? (err instanceof Error ? err.message : "unknown")}`);
        } finally {
            setIsUploadingDoc(false);
        }
    };

    // The booking chip's Schedule button lands here with ?schedule=1: open the
    // schedule step once the job has loaded, then drop the flag from the URL.
    const scheduleParamDone = useRef(false);
    useEffect(() => {
        if (!job?.id || scheduleParamDone.current) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("schedule") !== "1") return;
        scheduleParamDone.current = true;
        setScheduleEditNonce((n) => n + 1);
        params.delete("schedule");
        const qs = params.toString();
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }, [job?.id]);

    // Close Out's "N reports, 0 tests" guard lands here with ?paper=1: bring the
    // devices into view and say what to press, then drop the flag (11/2).
    const paperParamDone = useRef(false);
    useEffect(() => {
        if (!job?.id || paperParamDone.current) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("paper") !== "1") return;
        paperParamDone.current = true;
        window.setTimeout(() => {
            document.getElementById("calm-devices")?.scrollIntoView({ behavior: "smooth", block: "start" });
            toast.info("Open each device, then press Use the signed paper to make its test from the report.");
        }, 300);
        params.delete("paper");
        const qs = params.toString();
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }, [job?.id]);

    // Scroll targets for the stage strip (chapter wrappers, so a collapsed
    // chapter can still be scrolled to and expanded).
    const scheduleRef = useRef<HTMLDivElement | null>(null);
    const resultsRef = useRef<HTMLDivElement | null>(null);
    const paperworkRef = useRef<HTMLDivElement | null>(null);
    const activityRef = useRef<HTMLDivElement | null>(null);

    if (isLoading) return <PageSpinner />;
    if (!job) {
        return (
            <DetailErrorState
                error={jobError}
                onRetry={() => void refetchJob()}
                notFoundTitle="Job not found"
                listHref="/jobs"
                listLabel="Back to jobs"
            />
        );
    }

    const stageStrip = deriveStages(job, testResults, linkedInvoices, linkedEstimates);
    const postVisit = isPostVisit(job.status);
    const actions = primaryHeaderActions(job.status, {
        jobId: job.id,
        customerId: job.customer_id,
        siteId: job.site_id,
        hasInvoices: linkedInvoices.length > 0,
    });

    // Story chapters (Issue #768): every attachment the paperwork card lists
    // counts as "paper exists" so the chapter opens the moment there's
    // anything to show.
    const documentCount =
        (documentsQ.data?.attachments.length ?? 0) +
        (testReportsQ.data?.attachments.length ?? 0) +
        (officialReportsQ.data?.attachments.length ?? 0) +
        (inspectionReportsQ.data?.attachments.length ?? 0) +
        (summaryReportsQ.data?.attachments.length ?? 0) +
        (workOrdersQ.data?.attachments.length ?? 0);
    const chapters = deriveChapters(job, {
        deviceCount: jobAssets.length,
        testCount: testResults.length,
        invoiceCount: linkedInvoices.length,
        estimateCount: linkedEstimates.length,
        documentCount,
    });
    const chapterPlan = (key: ChapterKey) => chapters.find((c) => c.key === key)!;
    const chapterExpanded = (key: ChapterKey) => chapterPrefs[key] ?? false;
    const toggleChapter = (key: ChapterKey) => setChapterPrefs({ ...chapterPrefs, [key]: !chapterExpanded(key) });
    const chapterRefs: Record<ChapterKey, React.RefObject<HTMLDivElement | null>> = {
        schedule: scheduleRef,
        results: resultsRef,
        paperwork: paperworkRef,
    };
    /** Expand a chapter (if it's compressed) and scroll to it — the stage
     *  pills and the NOW panel's money rows both land here. */
    const openChapter = (key: ChapterKey) => {
        if (chapterPlan(key).state !== "open" && !chapterExpanded(key)) {
            setChapterPrefs({ ...chapterPrefs, [key]: true });
        }
        chapterRefs[key].current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    /** Calm layout (#1717): scroll to a part of the work or side sheet. */
    const scrollToCalm = (elementId: string) =>
        document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    /** "Open the paperwork": the Files fold in the calm layout, else the chapter. */
    const openPaperwork = () => (flActive ? setFilesNonce((n) => n + 1) : openChapter("paperwork"));

    const scrollToStage = (key: JobStage["key"]) => {
        // When Close Out is the *current* (highlighted) stage the job is
        // sitting in the closeout queue — clicking that pill jumps straight to
        // this job in Closeout rather than scrolling to its paperwork. Other
        // stages (and a done/pending Close Out) keep the scroll-to-evidence
        // behavior, since those jobs aren't in the closeout queue.
        if (key === "close_out" && stageStrip.stages.find((s) => s.key === "close_out")?.state === "current") {
            navigate(`/close-out?job=${job.id}`);
            return;
        }
        // Pill click scrolls AND expands its chapter (Issue #768 region J1).
        if (flActive) {
            const chapter = STAGE_CHAPTER[key];
            if (chapter === "schedule") setScheduleEditNonce((n) => n + 1);
            else if (chapter === "results") scrollToCalm("calm-devices");
            else openPaperwork();
            return;
        }
        openChapter(STAGE_CHAPTER[key]);
    };

    /** A Finish Line link back to this page opens the part it names. */
    const openFinishLineSection = (section: FinishLineSection) => {
        if (flActive) {
            if (section === "devices") scrollToCalm("calm-devices");
            else if (section === "paperwork") openPaperwork();
            else if (section === "schedule") setScheduleEditNonce((n) => n + 1);
            else scrollToCalm("calm-contact");
            return;
        }
        if (section === "devices") openChapter("results");
        else if (section === "paperwork") openChapter("paperwork");
        else if (section === "schedule") setScheduleEditNonce((n) => n + 1);
        else
            document
                .querySelector('[data-testid="customer-site-card"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // With the Finish Line on, the header's filled status buttons step aside
    // (its Next is the page's one filled button). Status moves go to the top
    // of ⋯; the hand-back kinds (send back, reopen, cancel) stay as quiet text
    // buttons so nothing becomes unreachable.
    const headerPrimary: HeaderAction[] = flActive
        ? actions.primary.filter((a) => a.kind === "send-back" || a.kind === "reopen" || a.kind === "cancel")
        : actions.primary;
    const menuPromoted: HeaderAction[] = flActive
        ? actions.primary.filter((a) => a.kind === "status" || a.kind === "link")
        : [];

    const runHeaderAction = (action: HeaderAction) => {
        if (action.kind === "status") {
            // "Schedule" on a job with no appointment opens the Schedule &
            // Crew editor instead of flipping the status chip (Prompt 21
            // 3c) — the server also rejects the bare flip with
            // SCHEDULE_REQUIRES_APPOINTMENT as a backstop.
            if (action.status === "scheduled" && !job.scheduled_start) {
                setScheduleEditNonce((n) => n + 1);
                toast.info("Set the appointment first — opening Schedule & Crew.");
                return;
            }
            updateStatus.mutate(action.status);
        } else if (action.kind === "cancel") setShowCancelDialog(true);
        else if (action.kind === "send-back") setShowSendBackDialog(true);
        else if (action.kind === "reopen") setShowReopenDialog(true);
    };

    const jobLabel = `Job #${job.display_number || job.id.slice(0, 8)}`;

    // Device add gating (mirrors the old JobDevicesPanel rules).
    const tradeHasFamily = assetKindsForTrade(job.trade_code ?? null) !== null;
    // #1300 — the tech workflow for this job's trade, linked from the Results
    // card. Office-only, and only while the job is still workable: once it is
    // closed or cancelled there is nothing to finish. Vendor work orders have
    // their own screens and are excluded.
    const fieldWorkflowHref =
        isOffice && job.kind !== "vendor_work_order" && !["closed", "cancelled"].includes(job.status)
            ? job.trade_code === "backflow"
                ? `/field/backflow/${job.id}`
                : job.trade_code === "fire_ext"
                  ? `/field/fire-safety/${job.id}`
                  : null
            : null;
    const addDisabled = !job.site_id || !tradeHasFamily;
    const addTitle = !job.site_id
        ? "This job has no site"
        : !tradeHasFamily
          ? "Set a trade on this job before linking devices"
          : "Add a device to this job";

    // #1717 — the same device callbacks the Results card gets, for the calm
    // Devices section.
    const calmDeviceActions = {
        onAddDevice: () => {
            setPickerTab("existing");
            setPickerOpen(true);
        },
        addDisabled,
        addTitle,
        onReconcile: job.trade_code === "backflow" && job.site_id ? () => setReconcileOpen(true) : null,
        onEditAsset: setEditingAsset,
        onUnlinkAsset: async (asset: JobAssetRow) => {
            const ok = await confirmDialog({
                message: `Remove "${asset.label}" from this job?`,
                destructive: true,
                confirmLabel: "Remove",
            });
            if (ok) removeAsset.mutate(asset.link_id);
        },
        unlinkPending: removeAsset.isPending,
        onScopeAsset: isOffice
            ? (asset: JobAssetRow, action: "test" | "repair" | "replace") =>
                  scopeAsset.mutate({ assetId: asset.asset_id, action })
            : undefined,
        scopePendingAssetId: scopeAsset.isPending ? (scopeAsset.variables?.assetId ?? null) : null,
        onUsePaper: canConvertPaper && job.trade_code === "backflow" ? setPaperAsset : undefined,
        onRecordRepair: canConvertPaper && job.trade_code === "backflow" ? setRepairAsset : undefined,
        recordRepairForName: (job.assigned_to_name ?? "").trim().split(/\s+/)[0] || null,
        onRemoveRepair: canConvertPaper && job.trade_code === "backflow" ? setRemoveRepairAsset : undefined,
        fieldWorkflowHref,
        // Job page v2 (slice 3): hover ✎ / ✕ / ⋯ and right-click on each row.
        rowTools: {
            jobId: job.id,
            siteId: job.site_id ?? null,
            onRemoveConfirmed: (asset: JobAssetRow) => removeAsset.mutate(asset.link_id),
        },
        // Proof chips S3: each leg's chip; "Upload the report" is this page's Upload.
        jobProof: jobProof.proof,
        onUploadReport: () => uploadInputRef.current?.click(),
    };

    // ── Job page v2: the one list of job actions (slice 1) ────────────────
    // Every id maps to the page's EXISTING handler: the same mutation, dialog
    // or link the classic page uses. Only read while the switch is on.
    const askCalm = (kind: CalmRequestKind) => setCalmRequest((r) => ({ kind, n: (r?.n ?? 0) + 1 }));
    const atRows = (section: string, words: string) => {
        scrollToCalm(section);
        toast.info(words);
    };
    const moveTo = (status: MoveTarget) => () => runHeaderAction({ kind: "status", status, label: status });
    const unsentInvoice = linkedInvoices.find(
        (inv) => (inv.emailed_count ?? 0) === 0 && !["paid", "void", "voided"].includes(inv.status),
    );
    const actionCtx: JobActionContext = {
        jobId: job.id,
        status: job.status,
        kind: job.kind ?? null,
        tradeCode: job.trade_code ?? null,
        customerId: job.customer_id ?? null,
        siteId: job.site_id ?? null,
        sourceEstimateId: job.source_estimate_id ?? null,
        scheduledStart: job.scheduled_start ?? null,
        confirmed: !!job.confirmed_at,
        hasInvoices: linkedInvoices.length > 0,
        hasUnsentInvoice: !!unsentInvoice,
        deviceCount: jobAssets.length,
        hasFailedDevice: testResults.some((t) => (t.overall_result ?? "").toLowerCase() === "fail"),
        hasRepair: repairResults.length > 0,
        waterAuthorityName: job.water_provider_display_name || job.water_provider_name || null,
        waterProviderId: job.water_provider_id ?? null,
        portalUrl: job.water_provider_portal_url ?? null,
        isPortal: !!job.water_provider_is_portal,
        fieldWorkflowHref,
        tradeHasFamily,
        hasWorkOrderMapping: !!jobResp?.workOrder,
        canResendPacket,
        perms: {
            isOffice,
            manageJobs: canManageJobs,
            manageOfficeReview: canConvertPaper,
            seePricing: canDo("pricing.view"),
            switchAllowed: flTop.switchAllowed,
        },
    };
    const docLink = (kind: "estimate" | "invoice") =>
        `/commercial/new?jobId=${job.id}&customerId=${job.customer_id ?? ""}` +
        (job.site_id ? `&siteId=${job.site_id}` : "") +
        `&kind=${kind}`;
    const openSchedule = () => setScheduleEditNonce((n) => n + 1);
    const toFinishLine = () =>
        document.querySelector('[data-testid="finish-line"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    const actionHandlers: JobActionHandlers = {
        "move:awaiting_scheduling": moveTo("awaiting_scheduling"),
        "move:scheduled": moveTo("scheduled"),
        "move:en_route": moveTo("en_route"),
        "move:arrived": moveTo("arrived"),
        "move:in_progress": moveTo("in_progress"),
        "move:office_review": moveTo("office_review"),
        "move:cannot_complete": moveTo("cannot_complete"),
        "move:held": () => setHoldAsk((n) => n + 1),
        approve: () => navigate("/office-review"),
        "send-back": () => setShowSendBackDialog(true),
        reopen: () => setShowReopenDialog(true),
        cancel: () => setShowCancelDialog(true),
        "open-items": toFinishLine,
        checkup: openHelpDesk,
        "edit-title": () => {
            setTitleInput(job.title);
            setEditingTitle(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        "copy-number": () =>
            void navigator.clipboard
                ?.writeText(job.display_number || job.id)
                .then(() => toast.success("Job number copied")),
        "open-customer": () => job.customer_id && navigate(`/customers/${job.customer_id}`),
        "open-site": () => job.site_id && navigate(`/sites/${job.site_id}`),
        "open-source-estimate": () => job.source_estimate_id && navigate(`/estimates/${job.source_estimate_id}`),
        task: () => setShowTaskDialog(true),
        "edit-job-form": () => navigate(`/jobs/${job.id}/edit`),
        "resend-packet": () => openResend(false),
        "classic-page": () => flTop.setSwitch(false),
        call: () => scrollToCalm("calm-contact"),
        "manage-contacts": () => job.customer_id && navigate(`/customers/${job.customer_id}`),
        "all-contacts": () => {
            scrollToCalm("calm-contact");
            askCalm("all-contacts");
        },
        visit: openSchedule,
        "add-helper": openSchedule,
        "confirm-booking": () => railMutations.toggleConfirm.mutate(!job.confirmed_at),
        "another-visit": openSchedule,
        "cannot-complete": openSchedule,
        "tech-brief": () => scrollToCalm("calm-side-sheet"),
        "visit-history": openSchedule,
        details: () => openDetails("details"),
        tags: () => openDetails("details"),
        "helper-value": () => openDetails("details"),
        "ai-summary": () => openDetails("details"),
        "edit-ask": () => openDetails("details"),
        "original-scope": () => openDetails("details"),
        "wo-mapping": () => openDetails("details"),
        "service-type": () => window.scrollTo({ top: 0, behavior: "smooth" }),
        "more-about": () => openDetails("details"),
        devices: () => scrollToCalm("calm-devices"),
        "add-device": () => {
            setPickerTab("existing");
            setPickerOpen(true);
        },
        reconcile: () => setReconcileOpen(true),
        "tech-workflow": () => fieldWorkflowHref && navigate(fieldWorkflowHref),
        "edit-device": () => atRows("calm-devices", "Open a device, then Edit device."),
        "remove-device": () => atRows("calm-devices", "Open a device, then Remove from job."),
        "visit-kind": () => atRows("calm-devices", "Open a device that isn't done yet to set Test, Repair or Replace."),
        "record-repair": () => atRows("calm-devices", "Open the device, then Record repair."),
        "remove-repair": () => atRows("calm-devices", "Open the repaired device, then Remove repair."),
        "use-paper": () => atRows("calm-devices", "Open the device, then Use the paper."),
        "fail-protocol": () => atRows("calm-devices", "The fail protocol is inside each failed device."),
        "quoted-devices": () => {
            setPickerTab("existing");
            setPickerOpen(true);
        },
        upload: () => uploadInputRef.current?.click(),
        "city-filing": () => scrollToCalm("calm-city"),
        files: () => askCalm("files"),
        "all-paperwork": () => askCalm("paperwork"),
        "make-official": () => askCalm("paperwork"),
        "rename-file": () => askCalm("files"),
        "remove-report": () => askCalm("paperwork"),
        "delete-document": () => askCalm("files"),
        "download-zip": () => askCalm("paperwork"),
        "regenerate-reports": () => askCalm("paperwork"),
        "wo-pdf": () => askCalm("paperwork"),
        "fix-wo-template": () => askCalm("paperwork"),
        "regenerate-wo": () => askCalm("paperwork"),
        "use-as-completed-wo": () => askCalm("paperwork"),
        "preview-file": () => askCalm("paperwork"),
        "download-file": () => askCalm("paperwork"),
        "packet-history": () => scrollToCalm("calm-customer"),
        photos: () => askCalm("photos"),
        "device-photos": () => atRows("calm-devices", "Open a device to see its photos."),
        money: () => scrollToCalm("calm-money"),
        "create-invoice": () => navigate(docLink("invoice")),
        "create-estimate": () => navigate(docLink("estimate")),
        "send-invoice": () => (unsentInvoice ? setSendInvoiceId(unsentInvoice.id) : scrollToCalm("calm-money")),
        "create-po": () => askCalm("vendors"),
        parts: () => askCalm("vendors"),
        "water-authority": () => {
            scrollToCalm("calm-city");
            askCalm("water-authority");
        },
        "price-check": () =>
            job.water_provider_id &&
            openPriceCheck({
                providerId: job.water_provider_id,
                providerName: job.water_provider_display_name || job.water_provider_name || undefined,
            }),
        portal: () => job.water_provider_portal_url && openExternalLink(job.water_provider_portal_url),
        "flag-report-input": () => scrollToCalm("calm-city"),
        "st-code": () => scrollToCalm("calm-city"),
        permits: () => askCalm("permit"),
        registration: () => askCalm("permit"),
        note: () => askCalm("note"),
        message: () => askCalm("message"),
        "log-call": () => askCalm("call"),
        activity: () => scrollToCalm("calm-conversation"),
        "pinned-note": () => scrollToCalm("calm-conversation"),
        "ask-help-desk": () => openSupportCopilot(),
    };
    const allJobActions: JobAction[] = flActive ? jobActions(actionCtx, actionHandlers) : [];
    jobActionsPub.current = flActive
        ? {
              jobId: job.id,
              jobLabel: `#${job.display_number || job.id.slice(0, 8)}`,
              actions: allJobActions,
              pins: jobPins.pins,
              pinsAreOfficeDefault: jobPins.isOfficeDefault,
              togglePin: jobPins.toggle,
              resetPins: jobPins.reset,
          }
        : null;
    /** Hold, with the why written to the job's notes (no hold-reason column exists). */
    const holdWithReason = (reason: string) =>
        updateStatus.mutate("held", {
            onSuccess: (result) => {
                if (result === null) return;
                toast.success("Job moved to Hold");
                api.post(`/api/jobs/${job.id}/notes`, { body: `On hold: ${reason.toLowerCase()}.` })
                    .then(() => qc.invalidateQueries({ queryKey: ["activity-timeline"] }))
                    .catch(() => toast.error("The job is on hold, but the reason didn't save as a note."));
            },
        });

    // Legacy point-pay devices (for jobs imported from the old location-spined
    // workflow). Shown in a separate table below the story column.
    const deviceColumns: Column<DeviceRow>[] = [
        {
            key: "serial_number",
            header: "Serial #",
            sortable: true,
            render: (r) => <span className="font-mono text-xs">{r.serial_number ?? "—"}</span>,
        },
        { key: "label", header: "Label", sortable: true },
        { key: "size_inches", header: "Size (in)", sortable: true },
        {
            key: "device_status",
            header: "Status",
            sortable: true,
            render: (r) => (
                <Badge className={statusColor(r.device_status)} variant="outline">
                    {r.device_status}
                </Badge>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            {makeOfficial.element}
            {matchAll.element}
            {isOffice && <BookingScheduleBanner jobId={job.id} status={job.status} />}
            {flActive && (
                <input
                    ref={uploadInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="job-action-upload-input"
                    onChange={(e) => {
                        void handleDocUpload(e.target.files);
                        e.target.value = "";
                    }}
                />
            )}
            {/* Whole-page drop is handled globally by the UniversalDropLayer in
                AppShell now (drop a report/WO on any page); a file dropped while
                on this job auto-files here. The old per-page PageDropZone was
                removed per COMMAND-CENTER-REDESIGN.md decision 5. */}
            {/* Job page v3 — Job at a glance (approved 2026-10-09): with the
                new-page switch on, one dark header replaces the sticky header
                below (breadcrumb, title, vitals, lineage, pins, stage strip).
                Everything those offered is reachable here (GLANCE_REACH). */}
            {/* Teach cards (#1838): the new job page explains itself once. */}
            <TeachIntro featureKey="job-page.new" when={flActive} />
            {/* Office: gold dots on new-job-page buttons never used (max 3). */}
            {flActive && <TeachDots />}
            {/* #1897 — the paper names another vendor / moved today, Undo. */}
            <JobCustomerCheck jobId={job.id} />
            {/* Hand to… (threads/hand-it-off): Closeout jobs only. */}
            {isOffice && <JobHandoffStrip jobId={job.id} jobName={jobLabel} />}
            {/* The Quiet Toolbar (10/10): the job's marks, on the job. */}
            <JobMarksStrip job={job} canHandleFlag={canManageJobs} />
            {/* Card hold (#2037): Held / Won't hold / Released / Charged / No hold. */}
            {isOffice && <JobCardHoldPanel jobId={job.id} />}
            {/* "Check the site" (2026-10-29): amber, held from dispatch until confirmed. */}
            <SiteCheckPick jobId={job.id} needed={!!job.site_check_needed} canConfirm={canManageJobs} />
            {/* "No work order yet" (2026-10-21): red, with one-tap Upload. */}
            {job.wo_missing && (
                <NoWorkOrderBanner
                    jobId={job.id}
                    isOffice={isOffice}
                    candidate={
                        (documentsQ.data?.attachments ?? [])
                            .filter((a) => /\.pdf$/i.test(a.file_name) && isWorkOrderLooking(a))
                            .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
                    }
                />
            )}
            {flActive && (
                <div className="space-y-2">
                    <JobGlance
                        job={job}
                        assets={jobAssets}
                        testResults={testResults}
                        repairResults={repairResults}
                        invoices={linkedInvoices}
                        estimates={linkedEstimates}
                        workOrder={jobResp?.workOrder ?? null}
                        finishLine={flTop.finishLine}
                        postVisit={postVisit}
                        strip={stageStrip}
                        onStageClick={scrollToStage}
                        actions={allJobActions}
                        pins={jobPins.pins}
                        pinsAreOfficeDefault={jobPins.isOfficeDefault}
                        onResetPins={jobPins.reset}
                        onHold={holdWithReason}
                        holdRequest={holdAsk}
                        onMoreForThisJob={openCommandPalette}
                        onClassic={() => flTop.setSwitch(false)}
                        onEditTitle={() => {
                            setTitleInput(job.title);
                            setEditingTitle(true);
                        }}
                        titleEditor={
                            editingTitle ? (
                                <div
                                    className="-mt-1.5 flex flex-wrap items-center gap-2"
                                    data-testid="glance-title-editor"
                                >
                                    <Input
                                        value={titleInput}
                                        onChange={(e) => setTitleInput(e.target.value)}
                                        className="h-9 max-w-xl bg-white text-[15px] font-semibold text-stone-900"
                                        aria-label="Job title"
                                        autoFocus
                                    />
                                    <Button
                                        size="sm"
                                        disabled={railMutations.updateJobFields.isPending || !titleInput.trim()}
                                        onClick={() =>
                                            railMutations.updateJobFields.mutate({ title: titleInput.trim() })
                                        }
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="bg-white text-stone-900"
                                        onClick={() => setEditingTitle(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : undefined
                        }
                        onCopyNumber={() =>
                            void navigator.clipboard
                                ?.writeText(job.display_number || job.id)
                                .then(() => toast.success("Job number copied"))
                        }
                        onSchedule={() => setScheduleEditNonce((n) => n + 1)}
                        onAddHelper={actionHandlers["add-helper"]}
                        onDevices={(group) => setDevicesFocus((f) => ({ group, n: (f?.n ?? 0) + 1 }))}
                        onPaperwork={openPaperwork}
                        classification={
                            job.kind === "vendor_work_order" ? (
                                <ServiceClassificationChip
                                    jobId={job.id}
                                    serviceType={job.work_order_briefing?.service_classification?.service_type}
                                    canEdit={isOffice}
                                    mismatch={
                                        !!job.work_order_briefing?.service_classification?.service_type &&
                                        !job.work_order_briefing.service_classification.service_type.startsWith(
                                            "backflow",
                                        ) &&
                                        testResults.length > 0
                                    }
                                />
                            ) : undefined
                        }
                        budget={
                            <BudgetChip
                                customerId={job.customer_id}
                                category={workCategory(job.job_type_name ?? job.title)}
                            />
                        }
                        timing={
                            <>
                                <ArrivalLag
                                    completedAt={job.completed_at}
                                    completedReceivedAt={job.completed_received_at}
                                />
                                <LastReachedUs
                                    status={job.status}
                                    lastSeenAt={job.assigned_tech_last_seen_at}
                                    techName={job.assigned_to_name}
                                />
                                <TimeOnSite
                                    onSiteMinutes={job.on_site_minutes}
                                    workStartedAt={job.work_started_at}
                                    completedAt={job.completed_at}
                                    status={job.status}
                                />
                            </>
                        }
                    />
                    <JobTaskFlagStrip jobId={job.id} />
                </div>
            )}
            {/* ── Sticky header: identity rows + contextual actions ───────── */}
            {!flActive && (
                <div className="sticky top-0 z-20 -mx-4 border-b bg-background px-4 pb-2 pt-2 md:-mx-6 md:px-6">
                    {/* flex-wrap (#1814): at phone width the page toggle drops to its own line. */}
                    <div className="flex flex-wrap items-start justify-between gap-3 sm:flex-nowrap">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <BackLink
                                    fallback="/jobs"
                                    fallbackLabel="Back"
                                    className="text-xs font-medium text-foreground/80 hover:text-primary inline-flex items-center gap-1"
                                    icon={<ArrowLeft className="h-3 w-3" />}
                                />
                                {editingTitle ? (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={titleInput}
                                            onChange={(e) => setTitleInput(e.target.value)}
                                            className="text-lg font-bold h-9 max-w-md"
                                            autoFocus
                                        />
                                        <Button
                                            size="sm"
                                            disabled={railMutations.updateJobFields.isPending || !titleInput.trim()}
                                            onClick={() =>
                                                railMutations.updateJobFields.mutate({ title: titleInput.trim() })
                                            }
                                        >
                                            Save
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setEditingTitle(false)}>
                                            Cancel
                                        </Button>
                                    </div>
                                ) : (
                                    <h1 className="text-xl font-black tracking-tight text-foreground flex items-center gap-2 truncate">
                                        {job.title}
                                        <button
                                            type="button"
                                            className="text-muted-foreground hover:text-primary shrink-0"
                                            onClick={() => {
                                                setTitleInput(job.title);
                                                setEditingTitle(true);
                                            }}
                                            aria-label="Edit title"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                    </h1>
                                )}
                                {/* Job number: first-class identity — big, bold, copy-on-click */}
                                <button
                                    type="button"
                                    className="select-all font-mono text-lg font-bold tracking-tight text-foreground hover:text-primary shrink-0"
                                    title="Click to copy job number"
                                    onClick={() => {
                                        void navigator.clipboard
                                            ?.writeText(job.display_number || job.id)
                                            .then(() => toast.success("Job number copied"));
                                    }}
                                >
                                    #{job.display_number || job.id.slice(0, 8)}
                                </button>
                                <Badge className={statusColor(job.status)} variant="outline">
                                    {job.status?.replace(/_/g, " ")}
                                </Badge>
                                {/* #823 — the derived device count sits beside the
                                    hand-typed title so a "(5)" the title claims can
                                    never stand uncontradicted. Red when the title
                                    disagrees with the linked devices. */}
                                {(() => {
                                    const mm = deviceCountMismatch(job.title, jobAssets.length);
                                    if (mm) {
                                        return (
                                            <Badge
                                                variant="destructive"
                                                className="shrink-0"
                                                title={`The title claims (${mm.titleSays}) but ${mm.attached} device${mm.attached === 1 ? " is" : "s are"} linked. Fix the device list in ${flActive ? "Devices below" : "Results"}, or the title.`}
                                            >
                                                title ({mm.titleSays}) ≠ {mm.attached} attached
                                            </Badge>
                                        );
                                    }
                                    if (jobAssets.length === 0 && tradeExpectsDevices(job.trade_name)) {
                                        return (
                                            <Badge
                                                variant="outline"
                                                className="shrink-0 border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                                title={`No devices linked — the tech lands with nothing to test. Add them in ${flActive ? "Devices below" : "the Results card"}.`}
                                            >
                                                0 devices
                                            </Badge>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            {/* Facts row: customer · address · tech · date */}
                            <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-foreground/80">
                                {job.company_name && (
                                    <Link
                                        to={`/customers/${job.customer_id}`}
                                        className="inline-flex items-center gap-1 hover:text-primary hover:underline"
                                    >
                                        <User className="h-3 w-3" /> {job.company_name}
                                    </Link>
                                )}
                                <BudgetChip
                                    customerId={job.customer_id}
                                    category={workCategory(job.job_type_name ?? job.title)}
                                />
                                {(job.site_address_line || job.location_address) &&
                                    (job.site_id ? (
                                        <Link
                                            to={`/sites/${job.site_id}`}
                                            className="inline-flex items-center gap-1 hover:text-primary hover:underline"
                                        >
                                            <MapPin className="h-3 w-3" />
                                            {job.site_address_line ?? job.location_address}
                                        </Link>
                                    ) : (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="h-3 w-3" />
                                            {job.site_address_line ?? job.location_address}
                                        </span>
                                    ))}
                                {job.assigned_to_name && (
                                    <span className="inline-flex items-center gap-1">
                                        <UserCheck className="h-3 w-3" /> {job.assigned_to_name}
                                    </span>
                                )}
                                {job.scheduled_start && (
                                    <span className="inline-flex items-center gap-1">
                                        <Calendar className="h-3 w-3" /> {formatDateTime(job.scheduled_start)}
                                    </span>
                                )}
                            </div>
                            {/* Vitals strip (2026-07-14): PO/WO · location phone ·
                                water account — pinned in the header, never buried
                                in cards. Amber MISSING = portal city, no account. */}
                            {(() => {
                                const poRef = job.customer_po?.trim();
                                const woRef = customerWoNumber(job, { includePo: false });
                                const vitalsPhone =
                                    job.site_contact_phone?.trim() ||
                                    job.site_phone?.trim() ||
                                    job.customer_phone?.trim();
                                // #824 (audit m5): this number sat unlabeled next to a
                                // DIFFERENT number in the contact card — say which line
                                // this one is instead of making the office guess.
                                const vitalsPhoneLabel = job.site_contact_phone?.trim()
                                    ? "site contact"
                                    : job.site_phone?.trim()
                                      ? "site line"
                                      : "customer";
                                const acctNo = job.site_water_account_number?.trim();
                                const hasProvider = !!(job.water_provider_id || job.water_provider_name);
                                if (!poRef && !woRef && !vitalsPhone && !hasProvider) return null;
                                return (
                                    <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs font-medium text-foreground/90">
                                        {poRef && <span className="tabular-nums">PO {poRef}</span>}
                                        {woRef && <span className="tabular-nums">WO {woRef}</span>}
                                        {/* #672: the intake classification steers completion
                                            gates and the WO fill — visible next to the WO
                                            number, amber when backflow evidence contradicts
                                            it, office-editable (audited). */}
                                        {job.kind === "vendor_work_order" && (
                                            <ServiceClassificationChip
                                                jobId={job.id}
                                                serviceType={
                                                    job.work_order_briefing?.service_classification?.service_type
                                                }
                                                canEdit={isOffice}
                                                mismatch={
                                                    !!job.work_order_briefing?.service_classification?.service_type &&
                                                    !job.work_order_briefing.service_classification.service_type.startsWith(
                                                        "backflow",
                                                    ) &&
                                                    testResults.length > 0
                                                }
                                            />
                                        )}
                                        {vitalsPhone && (
                                            <a
                                                href={`tel:${vitalsPhone}`}
                                                className="inline-flex items-center gap-1 tabular-nums hover:text-primary hover:underline"
                                                title="Location contact phone"
                                            >
                                                <Phone className="h-3 w-3" /> {vitalsPhone}
                                                <span className="font-normal text-foreground/55">
                                                    ({vitalsPhoneLabel})
                                                </span>
                                            </a>
                                        )}
                                        {hasProvider &&
                                            // Portal-first: a paper/offline jurisdiction has no portal
                                            // account, so a stored number is stale/legacy — never show it
                                            // (fall through to "no acct — paper"). Only portals surface
                                            // the account (or the amber "missing" nudge).
                                            (!job.water_provider_is_portal ? (
                                                <span
                                                    className="text-muted-foreground"
                                                    title="This jurisdiction files on paper or by mail — there is no online portal, so no account number applies"
                                                >
                                                    no acct — paper
                                                </span>
                                            ) : acctNo ? (
                                                <span className="tabular-nums" title="Water provider account #">
                                                    Acct {acctNo}
                                                </span>
                                            ) : (
                                                <span
                                                    className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                                                    title="Portal jurisdiction with no water account # on the site — add it before filing"
                                                >
                                                    acct missing
                                                </span>
                                            ))}
                                        {/* TAX VISIBILITY: the customer's tax chip rides the vitals strip. */}
                                        {job.customer_tax_status === "exempt" ? (
                                            <span
                                                className="rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase text-foreground/70"
                                                title={job.customer_tax_exempt_reason || "Tax exempt"}
                                            >
                                                tax exempt
                                            </span>
                                        ) : job.customer_tax_status === "taxable" ? (
                                            <span
                                                className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                                                title="Sales tax applies on this customer's invoices"
                                            >
                                                taxable
                                            </span>
                                        ) : job.customer_tax_status === "unknown" ? (
                                            <span
                                                className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                                                title="Customer tax status not set — invoice sends will hold until decided"
                                            >
                                                tax status?
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            })()}
                            {/* Paperwork lineage — the estimate this job came from
                                and the estimate(s)/invoice(s) it produced, pinned at
                                the top, links both ways. */}
                            <JobLineageStrip job={job} invoices={linkedInvoices} estimates={linkedEstimates} />
                        </div>
                        {/* Two contextual primaries + overflow ⋯ */}
                        {/* #1545 added a third button here, which at phone width
                            pushed the row past the viewport — `shrink-0` meant it
                            overflowed rather than reflowed. Wrapping (and dropping
                            shrink-0 below sm) keeps the actions on-screen at 390px;
                            the desktop row is unchanged. */}
                        {/* Job page v2: the new page's status, pins and ⋯ live in the
                            bar under the title (JobActionBar), not here. */}
                        {!flActive && (
                            <div className="flex flex-wrap items-center justify-end gap-2 sm:shrink-0 sm:flex-nowrap">
                                {/* #1545 — Resend packet, top of the page. It sits
                                OUTSIDE primaryHeaderActions on purpose: that helper
                                caps at two and would have bumped "Create invoice"
                                into the ⋯ menu on a finished job that has none.
                                Corbin's call — there's room on a desktop header, so
                                nothing gets bumped. Only ever shown once the packet
                                has actually gone out at least once. */}
                                {canResendPacket && (
                                    <Button
                                        variant={flActive ? "ghost" : "outline"}
                                        size="sm"
                                        onClick={() => openResend(false)}
                                        title="Email the customer's packet again — nothing is re-issued and no card is charged"
                                    >
                                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Resend packet
                                    </Button>
                                )}
                                {headerPrimary.map((a) =>
                                    a.kind === "link" ? (
                                        <Button key={a.label} variant="default" size="sm" asChild>
                                            <Link to={a.to}>{a.label}</Link>
                                        </Button>
                                    ) : (
                                        <Button
                                            key={a.label}
                                            variant={flActive ? "ghost" : a.label === "Hold" ? "outline" : "default"}
                                            size="sm"
                                            disabled={updateStatus.isPending}
                                            onClick={() => runHeaderAction(a)}
                                        >
                                            {a.label}
                                        </Button>
                                    ),
                                )}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" aria-label="More actions">
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        {menuPromoted.map((a, i) =>
                                            a.kind === "link" ? (
                                                <DropdownMenuItem key={`promoted-${a.label}-${i}`} asChild>
                                                    <Link to={a.to}>{a.label}</Link>
                                                </DropdownMenuItem>
                                            ) : (
                                                <DropdownMenuItem
                                                    key={`promoted-${a.label}-${i}`}
                                                    disabled={updateStatus.isPending}
                                                    onSelect={() => runHeaderAction(a)}
                                                >
                                                    {a.label}
                                                </DropdownMenuItem>
                                            ),
                                        )}
                                        {menuPromoted.length > 0 && actions.overflow.length > 0 && (
                                            <DropdownMenuSeparator />
                                        )}
                                        {actions.overflow.map((a, i) =>
                                            a.kind === "link" ? (
                                                <DropdownMenuItem key={`${a.label}-${i}`} asChild>
                                                    <Link to={a.to}>{a.label}</Link>
                                                </DropdownMenuItem>
                                            ) : a.kind === "cancel" ? (
                                                <DropdownMenuItem
                                                    key="cancel"
                                                    className="text-red-600 dark:text-red-400"
                                                    onSelect={() => setShowCancelDialog(true)}
                                                >
                                                    <Ban className="h-3.5 w-3.5 mr-2" /> {a.label}
                                                </DropdownMenuItem>
                                            ) : (
                                                <DropdownMenuItem
                                                    key={`${a.kind === "status" ? a.status : a.kind}-${i}`}
                                                    disabled={updateStatus.isPending}
                                                    onSelect={() => runHeaderAction(a)}
                                                >
                                                    {a.label}
                                                </DropdownMenuItem>
                                            ),
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onSelect={() => setShowTaskDialog(true)}>
                                            <Plus className="h-3.5 w-3.5 mr-2" /> Task
                                        </DropdownMenuItem>
                                        {/* #1717 — the rail's Schedule & Crew and Details,
                                        in a drawer, while the calm layout is on. */}
                                        {flActive && (
                                            <DropdownMenuItem
                                                data-testid="job-details-item"
                                                onSelect={() => openDetails("details")}
                                            >
                                                <ClipboardList className="h-3.5 w-3.5 mr-2" /> Job details
                                            </DropdownMenuItem>
                                        )}
                                        {/* #1814 — the job page switch moved out of
                                        this menu to the Classic page / New page
                                        toggle at the top right: sitting right
                                        under "Task", one slip turned Darcy's page
                                        over. */}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}
                        {/* #1814 — Classic page ↔ New page in plain sight on the
                            classic page too (the new page's glance header has its
                            own, #1825). Per user, same preference. */}
                        {flTop.switchAllowed && (
                            <JobPageVersionToggle isNew={flTop.switchOn} onChange={flTop.setSwitch} />
                        )}
                    </div>
                    {/* Stage strip — click scrolls to the evidence card */}
                    <div className="flex flex-wrap items-center justify-between gap-x-4">
                        <StageStrip strip={stageStrip} onStageClick={scrollToStage} />
                        {/* C2-5 (#1314) — the job's own labour number, beside the
                            timeline it belongs to. Definition + the rule that it is
                            never summed with a workday: docs/bible/labour-numbers.md */}
                        <div className="flex items-center gap-4">
                            {/* #1513 — why this job landed on the board late. Renders
                                nothing unless the completion actually arrived after
                                the tap that made it. */}
                            <ArrivalLag
                                completedAt={job.completed_at}
                                completedReceivedAt={job.completed_received_at}
                            />
                            {/* #1513 — while the job is still being worked, whether
                                his phone has been out of contact long enough to
                                explain a quiet board. */}
                            <LastReachedUs
                                status={job.status}
                                lastSeenAt={job.assigned_tech_last_seen_at}
                                techName={job.assigned_to_name}
                            />
                            <TimeOnSite
                                onSiteMinutes={job.on_site_minutes}
                                workStartedAt={job.work_started_at}
                                completedAt={job.completed_at}
                                status={job.status}
                            />
                        </div>
                    </div>
                    {/* Work Inbox V2: full-width flag strip INSIDE the header when
                        open tasks are holding this job. Color = hottest task heat. */}
                    <JobTaskFlagStrip jobId={job.id} />
                    {/* #1669 — the Help Desk's check-up: "can't go out yet — N
                        things to fix [Show me]", before anyone has to ask. */}
                    {/* #1714 — with the Finish Line on, its Next and truth lines
                        carry this; no second amber strip. */}
                    {!flActive && (
                        <JobCheckupStrip
                            jobId={job.id}
                            totalCount={jobNeeds.ready ? jobNeeds.blockers.length : undefined}
                        />
                    )}
                </div>
            )}

            {/* #1030 — this account is on service hold. Jobs arrive here from
                paths that never saw the New Job warning (WO intake, leads,
                estimate conversion), so the job says it too — before anyone
                dispatches a truck against it. */}
            {job.customer_service_hold && (
                <div className="rounded-lg border border-destructive/60 bg-card px-4 py-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="text-sm min-w-0">
                        <p className="font-semibold text-destructive">
                            {job.company_name} is on service hold — Do Not Service
                        </p>
                        <p className="text-foreground/80 break-words">
                            {job.customer_service_hold_reason?.trim() ||
                                "No reason was recorded when this hold was placed."}
                        </p>
                    </div>
                </div>
            )}

            {/* The quote's lines changed after this job went out (or the
                office wrote its own scope): offer the quote's plain scope
                lines. Renders nothing when the job and its quote agree. */}
            {isOffice && job.source_estimate_id && <QuoteScopeOffer jobId={job.id} />}

            {/* 👷 What the tech will do (2026-10-22) — the ONE place the tech
                is told things. Always open, under the header, on the classic
                AND the new page; "On track" never folds it. */}
            <OfficeTechPlanCard jobId={job.id} jobStatus={job.status} proof={jobProof.proof} />

            {/* #1714 — the Finish Line: Next, the meter, the promise list. */}
            {flTop.state === "on" && flTop.finishLine && (
                <FinishLinePanel
                    finishLine={flTop.finishLine}
                    preVisit={isPreVisit(job)}
                    onSection={openFinishLineSection}
                    alsoOpen
                    checkup={<JobCheckupStrip jobId={job.id} />}
                />
            )}
            {flTop.state === "loading" && (
                <div
                    className="rounded-xl border bg-card px-4 py-6 text-sm text-muted-foreground sm:px-7"
                    role="status"
                    data-testid="finish-line-loading"
                >
                    Working out what&apos;s next…
                </div>
            )}

            {/* NOW panel (#767, story-first IA) — the top card: SUMMARY (the
                ask) + THE STORY (derived beats) + pinned tech note + what
                needs you + ONE next step + money. Replaces the old amber
                office-review banner and the mid-column What's Holding card. */}
            {!flActive && (
                <NowPanel
                    job={job}
                    isOffice={isOffice}
                    deviceCount={jobAssets.length}
                    invoices={linkedInvoices}
                    estimates={linkedEstimates}
                    testResults={testResults}
                    primaryAction={actions.primary[0] ?? null}
                    onRunAction={runHeaderAction}
                    onOpenPaperwork={() => openChapter("paperwork")}
                    onSendInvoice={setSendInvoiceId}
                    onSetSchedule={() => setScheduleEditNonce((n) => n + 1)}
                    onAddDevices={() => {
                        setPickerTab("existing");
                        setPickerOpen(true);
                    }}
                    onResendMissed={() => openResend(true)}
                    packetMissedOnLastSend={sendHistoryQ.data?.missedOnLastSend ?? null}
                    finishLineMode={flActive}
                    deliveredAnotherWay={jobResp?.deliveredAnotherWay ?? null}
                    summarySlot={null}
                />
            )}

            {/* Multi-visit toggle (#293) moved to the rail's Schedule & Crew
                card (#769) — scheduling decisions live with the schedule. */}

            {/* #1300 — the rip cord. Records the signed paper as the device's
                result when the app workflow never finished. */}
            <PaperConversionDialog
                open={paperAsset != null}
                onClose={() => setPaperAsset(null)}
                jobId={job.id}
                asset={paperAsset}
                techs={techsQ.data?.users ?? []}
                defaultTesterId={job.assigned_to ?? null}
            />

            {/* Office "Record repair for <tech>" — unsticks a repair job whose
                tech never saved the repair (mockups/repair-without-photos.html). */}
            <RecordRepairDialog
                open={repairAsset != null}
                onClose={() => setRepairAsset(null)}
                jobId={job.id}
                asset={repairAsset}
                techs={techsQ.data?.users ?? []}
                defaultTechId={job.assigned_to ?? null}
                isMckinneySite={(job.water_provider_slug ?? "").trim().toLowerCase() === "mckinney"}
            />

            <RemoveRepairDialog
                open={removeRepairAsset != null}
                onClose={() => setRemoveRepairAsset(null)}
                jobId={job.id}
                asset={removeRepairAsset}
                repair={
                    removeRepairAsset
                        ? ([...repairResults].reverse().find((r) => r.asset_id === removeRepairAsset.asset_id) ?? null)
                        : null
                }
            />

            <CancelJobDialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                jobId={job.id}
                jobLabel={jobLabel}
            />

            <ReopenJobDialog
                open={showReopenDialog}
                onClose={() => setShowReopenDialog(false)}
                jobId={job.id}
                jobLabel={jobLabel}
            />

            <SendBackToFieldDialog
                open={showSendBackDialog}
                onClose={() => setShowSendBackDialog(false)}
                jobId={job.id}
                jobLabel={jobLabel}
                techName={job.assigned_to_name ?? null}
            />

            <TaskCreateDialog
                open={showTaskDialog}
                onOpenChange={setShowTaskDialog}
                prefill={{
                    title: `${jobLabel} — `,
                    links: [
                        { entityType: "job", entityId: job.id, label: jobLabel },
                        ...(job.customer_id
                            ? [
                                  {
                                      entityType: "customer",
                                      entityId: job.customer_id,
                                      label: job.company_name ?? "Customer",
                                  },
                              ]
                            : []),
                    ],
                }}
            />

            {sendInvoiceId && (
                <SendCustomerDialog
                    docType="invoice"
                    id={sendInvoiceId}
                    customerId={job.customer_id ?? null}
                    open={!!sendInvoiceId}
                    onOpenChange={(o) => !o && setSendInvoiceId(null)}
                    onSent={() => invalidateJob()}
                />
            )}

            {/* #1545 — one resend dialog for all three entry points. */}
            <ResendPacketDialog
                open={resendOpen}
                onClose={() => setResendOpen(false)}
                jobId={job.id}
                customerId={job.customer_id ?? null}
                defaultSubject={packetSubjectFor(job)}
                startInMissedMode={resendOnlyMissed}
                onSent={() => invalidateJob()}
                // #1569: "Go to Paperwork" on the not-ready notice. The card is
                // already on this page, so close the dialog and scroll to it
                // rather than navigating.
                onGoToPaperwork={() => {
                    setResendOpen(false);
                    openPaperwork();
                }}
            />

            {/* Open tasks linked to this job */}
            <TaskListInline entityType="job" entityId={job.id} />

            {/* Add from the field: everything a tech added during this job,
                wherever he sent it. Move re-points one. */}
            <FromTheFieldSection kind="job" id={job.id} />

            {/* #1717 — the switch on: the calm lower half. Off: today's page. */}
            {flActive ? (
                <>
                    {jobProof.allowed && <JobProofControls on={jobProof.on} set={jobProof.set} />}
                    <CalmJobBody
                        job={job}
                        finishLine={flTop.finishLine}
                        postVisit={postVisit}
                        preVisit={isPreVisit(job)}
                        isOffice={isOffice}
                        assets={jobAssets}
                        testResults={testResults}
                        repairResults={repairResults}
                        tradeHasFamily={tradeHasFamily}
                        failProtocols={failProtocols}
                        deviceActions={calmDeviceActions}
                        invoices={linkedInvoices}
                        estimates={linkedEstimates}
                        onSendInvoice={setSendInvoiceId}
                        sendHistory={sendHistoryQ.data ?? null}
                        onResend={canResendPacket ? () => openResend(false) : undefined}
                        documents={documentsQ.data?.attachments ?? []}
                        closeoutUploads={closeoutUploadsQ.data?.attachments ?? []}
                        uploadedWorkOrders={uploadedWorkOrdersQ.data?.attachments ?? []}
                        summaryReports={summaryReportsQ.data?.attachments ?? []}
                        reportCount={
                            (testReportsQ.data?.attachments.length ?? 0) +
                            (officialReportsQ.data?.attachments.length ?? 0)
                        }
                        isUploading={isUploadingDoc}
                        onUpload={(files) => void handleDocUpload(files)}
                        onDeleteDocument={(attachmentId) => deleteDoc.mutate(attachmentId)}
                        deletePending={deleteDoc.isPending}
                        onOpenDetails={() => openDetails("details")}
                        filesNonce={filesNonce}
                        request={calmRequest}
                        devicesFocus={devicesFocus}
                        detailsAsk={detailsMode}
                        newInvoiceHref={docLink("invoice")}
                        newEstimateHref={docLink("estimate")}
                        visitCard={
                            <ScheduleCrewCard
                                job={job}
                                postVisit={postVisit}
                                techs={techsQ.data?.users ?? []}
                                techsLoading={techsQ.isLoading}
                                m={railMutations}
                                editNonce={scheduleEditNonce}
                            />
                        }
                        detailsCards={
                            <>
                                {jobResp?.workOrder && <WorkOrderMappingCard wo={jobResp.workOrder} />}
                                <DetailsCard
                                    job={job}
                                    jobTypes={jobTypesQ.data?.jobTypes ?? []}
                                    jobTypesLoading={jobTypesQ.isLoading}
                                    onWantJobTypes={() => setWantJobTypes(true)}
                                    m={railMutations}
                                />
                                {/* The summary moved to "What the tech will do" at
                                    the top of the page (2026-10-22). */}
                                <JobReplacementsPanel jobId={id!} />
                                {legacyDevices.length > 0 && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-sm">Legacy devices</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <DataTable
                                                columns={deviceColumns}
                                                data={legacyDevices}
                                                keyField="device_id"
                                                sortId="jobs.detail.legacy-devices"
                                                loading={false}
                                                emptyMessage="No legacy devices"
                                            />
                                        </CardContent>
                                    </Card>
                                )}
                                <JobWarrantiesPanel jobId={id!} />
                            </>
                        }
                        paperworkCard={
                            <PaperworkCard
                                jobId={job.id}
                                jobNumber={job.display_number ?? null}
                                customerName={job.company_name ?? null}
                                tradeCode={job.trade_code ?? null}
                                documents={documentsQ.data?.attachments ?? []}
                                testReports={testReportsQ.data?.attachments ?? []}
                                officialReports={officialReportsQ.data?.attachments ?? []}
                                inspectionReports={inspectionReportsQ.data?.attachments ?? []}
                                summaryReports={summaryReportsQ.data?.attachments ?? []}
                                workOrders={workOrdersQ.data?.attachments ?? []}
                                closeoutUploads={closeoutUploadsQ.data?.attachments ?? []}
                                uploadedWorkOrders={uploadedWorkOrdersQ.data?.attachments ?? []}
                                woNumber={job.briefing_wo_number ?? null}
                                isWorkOrderJob={job.kind === "vendor_work_order"}
                                customerId={job.customer_id ?? null}
                                assignedTemplateId={jobResp?.workOrder?.template_id ?? null}
                                invoices={linkedInvoices}
                                estimates={linkedEstimates}
                                isUploading={isUploadingDoc}
                                onUpload={(files) => void handleDocUpload(files)}
                                onDeleteDocument={(attachmentId) => deleteDoc.mutate(attachmentId)}
                                deletePending={deleteDoc.isPending}
                                onSendInvoice={setSendInvoiceId}
                                sendHistory={sendHistoryQ.data ?? null}
                                onResendPacket={canResendPacket ? () => openResend(false) : undefined}
                                onMatchReports={matchAll.open}
                                devicePaperwork={devicePaperwork}
                                rowMenus
                            />
                        }
                        photosCard={<JobPhotosCard jobId={job.id} />}
                        vendorCards={
                            <>
                                {isOffice && (
                                    <JobPoCard
                                        jobId={job.id}
                                        sourceEstimateId={job.source_estimate_id ?? null}
                                        jobStatus={job.status}
                                    />
                                )}
                                <JobPartsCard jobId={job.id} canManage={isOffice} />
                            </>
                        }
                        permitCard={
                            <>
                                {isOffice && <AfterHoursCard jobId={job.id} canManage={canManageJobs} />}
                                <PermitCard jobId={job.id} canManage={canManageJobs} />
                                {/* City registrations: warn only, never a hold. */}
                                {isOffice && <RegistrationWarningsCard jobId={job.id} />}
                            </>
                        }
                    />
                </>
            ) : (
                <>
                    {/* ── Two columns: story left, reference rail right ───────────── */}
                    <div className="grid gap-4 xl:grid-cols-[2fr_1fr] items-start">
                        <div className="space-y-4 min-w-0">
                            {/* Summary + "What's holding this up" moved to the NOW
                        panel above (#767) — the ask, the story, the blockers,
                        the next step, and the money all answer first, in one
                        card, before the page tells the rest of the story. */}

                            {/* Return-PDF mapping now lives in ONE place — a single card in
                        the reference rail (loud while unverified, quiet once
                        confirmed) — so the "Review / fix PDF mapping" entry point no
                        longer jumps between the main column and the rail. */}

                            {/* ── Story chapters (Issue #768): each stage owns a block.
                        Done stages compress to one-line receipts, the current
                        stage's block is open, future stages are muted ghost
                        rows — all expandable, expansion persisted per user. ── */}

                            {/* Schedule chapter — always a compressed line (the plan
                        lives in the NOW panel + rail); expands to the booking
                        and visit facts. */}
                            <div ref={scheduleRef}>
                                <ChapterShell
                                    plan={chapterPlan("schedule")}
                                    open={chapterExpanded("schedule")}
                                    onToggle={() => toggleChapter("schedule")}
                                >
                                    <Card>
                                        <CardContent className="py-3">
                                            {chapterPlan("schedule").detail.length > 0 ? (
                                                <ul className="space-y-1 text-xs text-foreground/80">
                                                    {chapterPlan("schedule").detail.map((line) => (
                                                        <li key={line}>{line}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-xs text-foreground/60">
                                                    No appointment yet — set it in Schedule &amp; Crew on the right.
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                </ChapterShell>
                            </div>

                            <div ref={resultsRef}>
                                <ChapterShell
                                    plan={chapterPlan("results")}
                                    open={chapterExpanded("results")}
                                    onToggle={() => toggleChapter("results")}
                                >
                                    {jobProof.allowed && (
                                        <div className="mb-2">
                                            <JobProofControls on={jobProof.on} set={jobProof.set} />
                                        </div>
                                    )}
                                    <ResultsCard
                                        jobProof={jobProof.proof}
                                        onUploadReport={openPaperwork}
                                        assets={jobAssets}
                                        testResults={testResults}
                                        repairResults={repairResults}
                                        preVisit={!postVisit}
                                        tradeHasFamily={tradeHasFamily}
                                        addDisabled={addDisabled}
                                        addTitle={addTitle}
                                        onAddDevice={() => {
                                            setPickerTab("existing");
                                            setPickerOpen(true);
                                        }}
                                        onReconcile={
                                            job.trade_code === "backflow" && job.site_id
                                                ? () => setReconcileOpen(true)
                                                : null
                                        }
                                        quotedDevicesNudge={!!job.source_estimate_id}
                                        onEditAsset={setEditingAsset}
                                        onUnlinkAsset={async (asset) => {
                                            const ok = await confirmDialog({
                                                message: `Remove "${asset.label}" from this job?`,
                                                destructive: true,
                                                confirmLabel: "Remove",
                                            });
                                            if (ok) removeAsset.mutate(asset.link_id);
                                        }}
                                        unlinkPending={removeAsset.isPending}
                                        failProtocols={failProtocols}
                                        onScopeAsset={
                                            isOffice
                                                ? (asset, action) =>
                                                      scopeAsset.mutate({ assetId: asset.asset_id, action })
                                                : undefined
                                        }
                                        scopePendingAssetId={
                                            scopeAsset.isPending ? (scopeAsset.variables?.assetId ?? null) : null
                                        }
                                        onUsePaper={
                                            canConvertPaper && job.trade_code === "backflow" ? setPaperAsset : undefined
                                        }
                                        onRecordRepair={
                                            canConvertPaper && job.trade_code === "backflow"
                                                ? setRepairAsset
                                                : undefined
                                        }
                                        recordRepairForName={
                                            (job.assigned_to_name ?? "").trim().split(/\s+/)[0] || null
                                        }
                                        onRemoveRepair={
                                            canConvertPaper && job.trade_code === "backflow"
                                                ? setRemoveRepairAsset
                                                : undefined
                                        }
                                        fieldWorkflowHref={fieldWorkflowHref}
                                    />
                                </ChapterShell>
                            </div>

                            {/* Attached purchase order(s) (Issue #181) — surfaces an
                        in-flight PO from draft onward; renders nothing when the
                        job has no PO. Office-only (server route is gated too). */}
                            {isOffice && (
                                <JobPoCard
                                    jobId={job.id}
                                    sourceEstimateId={job.source_estimate_id ?? null}
                                    jobStatus={job.status}
                                />
                            )}

                            {/* Permit answers (#1250) — renders nothing unless this
                        job's jurisdiction actually asks for a permit. It gates
                        the job CLOSING; it never gated the tech's test. */}
                            {/* After hours (trust audit Phase 1): from the booking
                        or the quote, never the tech's start time; office can
                        override. */}
                            {isOffice && <AfterHoursCard jobId={job.id} canManage={canManageJobs} />}

                            <PermitCard jobId={job.id} canManage={canManageJobs} />

                            {/* City registrations (phase 1): what this job's city is
                        missing for its tester. Warn only — never a hold. */}
                            {isOffice && <RegistrationWarningsCard jobId={job.id} />}

                            {/* Tracked parts w/ custody state chips (§V2 item 3c) —
                        renders nothing when the job has no tracked parts. */}
                            <JobPartsCard jobId={job.id} canManage={isOffice} />

                            <div ref={paperworkRef}>
                                <ChapterShell
                                    plan={chapterPlan("paperwork")}
                                    open={chapterExpanded("paperwork")}
                                    onToggle={() => toggleChapter("paperwork")}
                                >
                                    <PaperworkCard
                                        jobId={job.id}
                                        jobNumber={job.display_number ?? null}
                                        customerName={job.company_name ?? null}
                                        tradeCode={job.trade_code ?? null}
                                        documents={documentsQ.data?.attachments ?? []}
                                        testReports={testReportsQ.data?.attachments ?? []}
                                        officialReports={officialReportsQ.data?.attachments ?? []}
                                        inspectionReports={inspectionReportsQ.data?.attachments ?? []}
                                        summaryReports={summaryReportsQ.data?.attachments ?? []}
                                        workOrders={workOrdersQ.data?.attachments ?? []}
                                        closeoutUploads={closeoutUploadsQ.data?.attachments ?? []}
                                        uploadedWorkOrders={uploadedWorkOrdersQ.data?.attachments ?? []}
                                        woNumber={job.briefing_wo_number ?? null}
                                        isWorkOrderJob={job.kind === "vendor_work_order"}
                                        customerId={job.customer_id ?? null}
                                        assignedTemplateId={jobResp?.workOrder?.template_id ?? null}
                                        invoices={linkedInvoices}
                                        estimates={linkedEstimates}
                                        isUploading={isUploadingDoc}
                                        onUpload={(files) => void handleDocUpload(files)}
                                        onDeleteDocument={(attachmentId) => deleteDoc.mutate(attachmentId)}
                                        deletePending={deleteDoc.isPending}
                                        onSendInvoice={setSendInvoiceId}
                                        sendHistory={sendHistoryQ.data ?? null}
                                        onResendPacket={canResendPacket ? () => openResend(false) : undefined}
                                        onMatchReports={matchAll.open}
                                        devicePaperwork={devicePaperwork}
                                    />
                                </ChapterShell>
                            </div>

                            {/* Field-captured job photos (category='photo'). The office
                        page never queried this category, so a tech's on-site
                        photos only showed as filenames in the Activity feed —
                        this gallery makes them viewable. Renders nothing when
                        the job has no photos. */}
                            <JobPhotosCard jobId={job.id} />

                            <ActivityCard ref={activityRef} job={job} />

                            {/* Demotions (Issue #768 region J7):
                        replacements, legacy devices, and warranties fold into
                        one collapsed group. Each section still self-suppresses
                        when empty (the body stays mounted so their queries and
                        empty-state rules behave exactly as before). */}
                            <Card>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-foreground/70"
                                    onClick={() => setMoreOpen((o) => !o)}
                                    aria-expanded={moreOpen}
                                    data-testid="more-about-job"
                                >
                                    {moreOpen ? (
                                        <ChevronDown className="h-4 w-4 shrink-0 text-foreground/40" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4 shrink-0 text-foreground/40" />
                                    )}
                                    More about this job — replacements · legacy devices · warranties
                                </button>
                            </Card>
                            <div className={cn("space-y-4", !moreOpen && "hidden")}>
                                {/* #825 M4: the expander never opens onto nothing.
                            (2026-10-22: the "Description" card is gone — jobs
                            have no description column, so it could never show
                            anything.) */}
                                {legacyDevices.length === 0 && (
                                    <Card>
                                        <CardContent
                                            className="py-3 text-xs text-foreground/60"
                                            data-testid="more-empty-note"
                                        >
                                            Empty sections stay hidden — replacement history, legacy devices, or
                                            warranties will appear here once this job has any.
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Replacements summary (only renders when at least one
                            backflow_replacement_results row exists for this job). */}
                                <JobReplacementsPanel jobId={id!} />

                                {legacyDevices.length > 0 && (
                                    <div>
                                        <h2 className="text-lg font-semibold mb-3 text-shell-foreground">
                                            Legacy devices
                                        </h2>
                                        <p className="text-xs text-shell-foreground/80 mb-2">
                                            This job was imported from the older location-spined workflow. Newly added
                                            devices go into the modern list above.
                                        </p>
                                        <DataTable
                                            columns={deviceColumns}
                                            data={legacyDevices}
                                            keyField="device_id"
                                            sortId="jobs.detail.legacy-devices"
                                            loading={false}
                                            emptyMessage="No legacy devices"
                                        />
                                    </div>
                                )}

                                {/* Warranties — renders nothing until a serialized part exists */}
                                <JobWarrantiesPanel jobId={id!} />
                            </div>
                        </div>

                        <ReferenceRail
                            job={job}
                            postVisit={postVisit}
                            techs={techsQ.data?.users ?? []}
                            techsLoading={techsQ.isLoading}
                            jobTypes={jobTypesQ.data?.jobTypes ?? []}
                            jobTypesLoading={jobTypesQ.isLoading}
                            onWantJobTypes={() => setWantJobTypes(true)}
                            mutations={railMutations}
                            workOrder={jobResp?.workOrder ?? null}
                            scheduleEditNonce={scheduleEditNonce}
                        />
                    </div>
                </>
            )}

            {pickerOpen && job.site_id && tradeHasFamily && (
                <AddDevicePicker
                    jobId={job.id}
                    siteId={job.site_id}
                    tradeCode={job.trade_code ?? null}
                    siteLat={job.site_latitude != null ? Number(job.site_latitude) : null}
                    siteLng={job.site_longitude != null ? Number(job.site_longitude) : null}
                    currentTab={pickerTab}
                    onTabChange={setPickerTab}
                    linkedAssetIds={new Set(jobAssets.map((a) => a.asset_id))}
                    onClose={() => setPickerOpen(false)}
                />
            )}

            {editingAsset && (
                <EditDeviceModal
                    jobId={job.id}
                    siteId={job.site_id ?? null}
                    asset={editingAsset}
                    onClose={() => setEditingAsset(null)}
                />
            )}

            {reconcileOpen && job.site_id && (
                <ReconcileFromListDialog
                    siteId={job.site_id}
                    jobId={job.id}
                    open={reconcileOpen}
                    onClose={() => {
                        setReconcileOpen(false);
                        invalidateJob();
                    }}
                />
            )}
            {/* #1431 — "Before you assign this", the same dialog as the Grid. */}
            {assignmentDialog}
        </div>
    );
}

interface JobWarrantyRow {
    serial_id: string;
    serial_number: string;
    sku: string;
    item_name: string;
    installed_at: string | null;
    effective_warranty_end: string | null;
    days_until_expiration: number | null;
    alert_status: "active" | "expiring_quarter" | "expiring_soon" | "expired";
}

// ──────────────────────────────────────────────────────────────────────────
// Replacements summary panel
//
// One block per `backflow_replacement_results` row on the job. For
// detector replacements (DCDA / RPDA / Type II) the same job carries TWO
// rows — `pair_role='main'` and `pair_role='bypass'` — and we group them
// under the parent's old_asset_id so the office sees one Old → New pair
// per physical assembly. The same component is shared with Office Review.
// ──────────────────────────────────────────────────────────────────────────

/** #1250 — the three one-tap reasons a tech can give instead of a picture. */
const SKIP_REASON_LABEL: Record<string, string> = {
    cant_reach: "couldn't get to it",
    already_replaced: "somebody else already replaced it",
    not_here: "it isn't there",
};

interface JobReplacementRow {
    id: string;
    job_id: string;
    old_asset_id: string;
    new_asset_id: string | null;
    old_serial: string | null;
    old_model: string | null;
    new_serial: string | null;
    new_model: string | null;
    pair_role: "main" | "bypass";
    parent_old_asset_id: string | null;
    prework_photos: string[] | null;
    /** #1250 — the AFTER picture, read from the new device's own test
     *  (its required `device` shot) rather than captured a second time. */
    after_photos: string[] | null;
    prework_notes: string | null;
    /** #1250 — why no before picture, when the tech couldn't take one. */
    before_skip_reason: string | null;
    before_skip_note: string | null;
    old_bypass_missing: boolean;
    replacement_date: string | null;
    finalized_at: string | null;
    performed_by: string | null;
    performed_by_name: string | null;
    created_at: string;
    updated_at: string;
    old_name: string | null;
    old_assembly_type: string | null;
    old_size: string | null;
    old_location: string | null;
    new_name: string | null;
    new_assembly_type: string | null;
    new_size: string | null;
    new_location: string | null;
}

function JobReplacementsPanel({ jobId }: { jobId: string }) {
    const q = useQuery<{ replacements: JobReplacementRow[] }>({
        queryKey: ["backflow", "job", jobId, "replacements"],
        queryFn: () => api.get(`/api/backflow/job/${jobId}/replacements`),
    });
    const rows = q.data?.replacements ?? [];
    if (rows.length === 0) return null;
    return <ReplacementsSummary rows={rows} />;
}

interface ReplacementsSummaryProps {
    rows: JobReplacementRow[];
    /** Caller can override the heading (e.g. "Replacement Summary" on the
     *  Office Review page) without rebuilding the body. */
    heading?: string;
}

function ReplacementsSummary({ rows, heading = "Replacements" }: ReplacementsSummaryProps) {
    // Group main + bypass rows by the main's old_asset_id so a detector
    // pair renders as a single block. Non-detector replacements have
    // pair_role='main' and parent_old_asset_id=null and stand alone.
    type Group = { main: JobReplacementRow | null; bypass: JobReplacementRow | null };
    const groups = new Map<string, Group>();
    for (const r of rows) {
        if (r.pair_role === "main") {
            const g = groups.get(r.old_asset_id) ?? { main: null, bypass: null };
            g.main = r;
            groups.set(r.old_asset_id, g);
        }
    }
    for (const r of rows) {
        if (r.pair_role === "bypass") {
            const parentKey = r.parent_old_asset_id ?? r.old_asset_id;
            const g = groups.get(parentKey) ?? { main: null, bypass: null };
            g.bypass = r;
            groups.set(parentKey, g);
        }
    }
    const ordered = Array.from(groups.values()).sort((a, b) => {
        const ta = (a.main ?? a.bypass)?.created_at ?? "";
        const tb = (b.main ?? b.bypass)?.created_at ?? "";
        return ta.localeCompare(tb);
    });
    // A row with no new device is a device the tech pictured at the gate, not a
    // replacement. Only the real swaps get a pair card.
    const swapped = ordered.filter((g) => (g.main ?? g.bypass)?.new_asset_id);
    const onlyPictured = ordered.filter((g) => !(g.main ?? g.bypass)?.new_asset_id);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    {heading}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {swapped.map((g, idx) => (
                    <ReplacementGroupCard key={(g.main ?? g.bypass)?.id ?? idx} group={g} />
                ))}
                {/* #1250 — the before-picture gate opens a row for EVERY device
                    it asks about, so "a row exists" no longer means "a swap
                    happened". Rendering those as replacement pairs showed the
                    office a New Main of "—" marked Pending forever, for devices
                    nobody touched. They belong in their own line, saying what
                    actually happened. */}
                {onlyPictured.length > 0 && (
                    <div className="rounded-md bg-muted/50 p-3 text-xs text-foreground/80">
                        {/* NOT "Photographed, not replaced" — this bucket is
                            every gate row with no new device, which includes the
                            ones the tech explicitly could NOT photograph, and the
                            reason is printed right after. */}
                        <span className="font-semibold text-foreground">Checked, not replaced:</span>{" "}
                        {onlyPictured
                            .map((g) => {
                                const r = (g.main ?? g.bypass)!;
                                const who = r.old_name ?? r.old_serial ?? "device";
                                const why = SKIP_REASON_LABEL[r.before_skip_reason ?? ""];
                                return why
                                    ? `${who} — ${why}${r.before_skip_note ? ` (${r.before_skip_note})` : ""}`
                                    : who;
                            })
                            .join(" · ")}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ReplacementGroupCard({
    group,
}: {
    group: { main: JobReplacementRow | null; bypass: JobReplacementRow | null };
}) {
    const main = group.main;
    const bypass = group.bypass;
    const primary = main ?? bypass;
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    if (!primary) return null;
    const photos = main?.prework_photos ?? bypass?.prework_photos ?? [];
    const afterPhotos = main?.after_photos ?? bypass?.after_photos ?? [];
    const notes = main?.prework_notes ?? bypass?.prework_notes ?? null;
    const bypassMissing = !!main?.old_bypass_missing;
    const finalizedAt = main?.finalized_at ?? bypass?.finalized_at ?? null;
    const performedByName = main?.performed_by_name ?? bypass?.performed_by_name ?? null;

    return (
        <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            {/* Old → New pair (main) */}
            {main && <ReplacementPairRow row={main} />}
            {/* Old → New pair (bypass) */}
            {bypass && <ReplacementPairRow row={bypass} />}

            {/* #1250 — Before and After, side by side. Both pictures already
                existed; they just lived on different records (the before on the
                replacement row, the after on the new device's test), so nobody
                could see them together — which is the only form in which they
                actually prove anything. */}
            {(photos.length > 0 || afterPhotos.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(
                        [
                            { label: "Before", list: photos, empty: beforeMissingLabel(main ?? bypass) },
                            { label: "After", list: afterPhotos, empty: "No photo of the new device on this test." },
                        ] as const
                    ).map((col) => (
                        <div key={col.label}>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 mb-1.5">
                                {col.label}
                                {col.list.length > 0 && <span className="ml-1 font-normal">({col.list.length})</span>}
                            </p>
                            {col.list.length > 0 ? (
                                <div className="grid grid-cols-3 gap-1.5">
                                    {col.list.map((src, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setLightboxSrc(src)}
                                            className="block aspect-square overflow-hidden rounded border bg-muted"
                                        >
                                            <img
                                                src={src}
                                                alt={`${col.label} ${i + 1}`}
                                                className="h-full w-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                /* STATES ALWAYS SPEAK: an empty half says WHY it
                                   is empty, so nobody reads a missing picture as
                                   a missing upload and goes hunting for it. */
                                <p className="rounded border border-dashed px-2 py-3 text-[11px] text-foreground/60">
                                    {col.empty}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Notes */}
            {notes && (
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 mb-1">
                        Replacement notes
                    </p>
                    <p className="text-xs whitespace-pre-wrap">{notes}</p>
                </div>
            )}

            {/* Bypass-missing banner */}
            {bypassMissing && (
                <div className="rounded border border-warning/40 bg-warning/10 p-2 text-[11px] text-warning-foreground flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Old bypass asset not found at prework time — verify replacement history.</span>
                </div>
            )}

            {/* Footer: who / when */}
            <div className="text-[11px] text-foreground/60 flex items-center gap-2 flex-wrap">
                {finalizedAt && <span>Replaced on {formatDateTime(finalizedAt)}</span>}
                {performedByName && <span>by {performedByName}</span>}
            </div>

            {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
        </div>
    );
}

/**
 * #1250 — why there is no before picture, in the tech's own words.
 *
 * The gate lets a tech move on with a one-tap reason instead of a photo, and
 * that reason is the answer to the question the office is about to ask. An
 * empty frame with no explanation reads as a failed upload and sends somebody
 * hunting for a picture that was never going to exist.
 */
function beforeMissingLabel(row: JobReplacementRow | null | undefined): string {
    switch (row?.before_skip_reason) {
        case "cant_reach":
            return "Tech couldn't get to the old device to photograph it.";
        case "already_replaced":
            return "Somebody else had already swapped this device before we arrived.";
        case "not_here":
            return "The old device wasn't there.";
        default:
            return "No before picture on this replacement.";
    }
}

function ReplacementPairRow({ row }: { row: JobReplacementRow }) {
    const finalized = !!row.finalized_at;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-0.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                    Old {row.pair_role === "bypass" ? "Bypass" : "Main"}
                    {row.old_assembly_type && (
                        <span className="ml-1 text-muted-foreground">· {row.old_assembly_type.toUpperCase()}</span>
                    )}
                </p>
                <p className="text-sm font-medium">{row.old_name ?? "—"}</p>
                {row.old_serial && <p className="text-xs text-foreground/80">SN {row.old_serial}</p>}
                {row.old_model && <p className="text-xs text-foreground/60">{row.old_model}</p>}
                {row.old_location && <p className="text-xs text-foreground/60">{row.old_location}</p>}
            </div>
            <div className="space-y-0.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60 flex items-center gap-1.5">
                    New {row.pair_role === "bypass" ? "Bypass" : "Main"}
                    {row.new_assembly_type && (
                        <span className="text-muted-foreground">· {row.new_assembly_type.toUpperCase()}</span>
                    )}
                    {finalized ? (
                        <Badge variant="success" className="text-[9px] px-1 py-0">
                            Finalized
                        </Badge>
                    ) : (
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">
                            Pending
                        </Badge>
                    )}
                </p>
                <p className="text-sm font-medium">{row.new_name ?? "—"}</p>
                {row.new_serial && <p className="text-xs text-foreground/80">SN {row.new_serial}</p>}
                {row.new_model && <p className="text-xs text-foreground/60">{row.new_model}</p>}
            </div>
        </div>
    );
}

function JobWarrantiesPanel({ jobId }: { jobId: string }) {
    const q = useQuery<{ warranties: JobWarrantyRow[] }>({
        queryKey: ["job", jobId, "warranties"],
        queryFn: () => api.get(`/api/jobs/${jobId}/warranties`),
    });
    const rows = q.data?.warranties ?? [];

    // Empty-state doctrine: no serialized parts installed → no card at all
    // (the section doesn't exist until it has something to say).
    if (q.isLoading || rows.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-sky-500" /> Warranties on installed parts
                </CardTitle>
            </CardHeader>
            <CardContent>
                <ul className="divide-y text-sm">
                    {rows.map((w) => (
                        <li key={w.serial_id} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-medium truncate">{w.item_name}</div>
                                <div className="text-xs text-foreground/80 font-mono">{w.serial_number}</div>
                            </div>
                            <div className="flex items-center gap-2 text-xs whitespace-nowrap">
                                {w.alert_status === "expired" && <AlertTriangle className="h-3 w-3 text-rose-600" />}
                                {(w.alert_status === "expiring_soon" || w.alert_status === "expiring_quarter") && (
                                    <Clock className="h-3 w-3 text-amber-600" />
                                )}
                                {w.alert_status === "active" && <ShieldCheck className="h-3 w-3 text-emerald-600" />}
                                <span>
                                    {w.effective_warranty_end ? formatDate(w.effective_warranty_end) : "—"}
                                    {w.days_until_expiration !== null && (
                                        <span className="text-foreground/80 ml-1">
                                            (
                                            {w.days_until_expiration < 0
                                                ? `${Math.abs(w.days_until_expiration)}d ago`
                                                : `${w.days_until_expiration}d left`}
                                            )
                                        </span>
                                    )}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}
